import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Phone, MessageSquare, Shield, Loader2, RotateCcw } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { OpenWhenNudge } from "@/components/letters/OpenWhenNudge";
import { MomentHelpers } from "@/components/playbook/MomentHelpers";

const PHASES = [["Breathe in", 1.0], ["Hold", 1.0], ["Breathe out", 0.55], ["Hold", 0.55]];
const SENSES = [[5, "things you can see"], [4, "things you can touch"], [3, "things you can hear"], [2, "things you can smell"], [1, "thing you can taste"]];
const logUse = (outcome) => api.post("/sos/use", { outcome }).catch(() => {});

const Breathe = ({ onDone }) => {
  const [t, setT] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setT((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const phase = Math.floor(t / 4) % 4;
  useEffect(() => { if (t % 4 === 0) navigator.vibrate?.(phase % 2 ? 40 : [60, 40, 60]); }, [t, phase]);
  useEffect(() => { if (t >= 60) onDone(); }, [t, onDone]);
  return (
    <div className="flex flex-col items-center text-center" data-testid="sos-breathe">
      <p className="text-white/60 text-sm">Step 1 of 3 · Breathe with me</p>
      <div className="relative w-64 h-64 my-10 flex items-center justify-center">
        <div className="absolute inset-0 rounded-full bg-[#7C5CBF]/25 transition-transform ease-in-out" style={{ transform: `scale(${PHASES[phase][1]})`, transitionDuration: "4000ms" }} aria-hidden />
        <div className="absolute inset-8 rounded-full bg-[#7C5CBF]/35 transition-transform ease-in-out" style={{ transform: `scale(${PHASES[phase][1]})`, transitionDuration: "4000ms" }} aria-hidden />
        <div className="relative text-2xl font-semibold text-white" aria-live="polite" data-testid="sos-breathe-phase">{PHASES[phase][0]}</div>
      </div>
      <p className="text-white/70">{60 - Math.min(t, 60)} seconds · in for 4, hold for 4, out for 4, hold for 4</p>
      <button onClick={onDone} className="mt-8 text-white/60 underline text-sm py-3 px-4" data-testid="sos-breathe-skip">Skip to grounding</button>
    </div>
  );
};

const Ground = ({ onDone }) => {
  const [i, setI] = useState(0);
  const [n, setN] = useState(0);
  const [note, setNote] = useState("");
  const [count, label] = SENSES[i];
  const tap = () => {
    setNote("");
    if (n + 1 < count) return setN(n + 1);
    if (i + 1 < SENSES.length) { setI(i + 1); setN(0); } else onDone();
  };
  return (
    <div className="flex flex-col items-center text-center w-full max-w-md" data-testid="sos-ground">
      <p className="text-white/60 text-sm">Step 2 of 3 · Ground yourself</p>
      <h2 className="text-3xl font-semibold text-white mt-6">Name {count} {label}</h2>
      <div className="flex gap-2 mt-6" aria-label={`${n} of ${count}`}>{Array.from({ length: count }, (_, k) => <span key={k} className={`w-3 h-3 rounded-full ${k < n ? "bg-[#B9A3E8]" : "bg-white/20"}`} />)}</div>
      <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={60} placeholder="Type it if you like (optional)" className="mt-8 w-full rounded-full bg-white/10 text-white placeholder:text-white/40 px-5 py-3 outline-none focus:ring-2 focus:ring-[#B9A3E8]" data-testid="sos-ground-input" />
      <button onClick={tap} className="mt-5 w-full rounded-full bg-[#7C5CBF] text-white text-lg font-semibold py-5 active:scale-[0.98] transition-transform" data-testid="sos-ground-tap">I noticed one</button>
    </div>
  );
};

const Unsafe = () => (
  <div className="w-full max-w-md text-center" data-testid="sos-unsafe">
    <h2 className="text-2xl font-semibold text-white">You don't have to go through this alone</h2>
    <p className="text-white/70 mt-2">Please reach out right now. It's free and open 24/7.</p>
    <div className="grid gap-3 mt-6">
      <a href="tel:988" className="rounded-full bg-white text-[#3A2E4F] font-bold py-4 flex items-center justify-center gap-2" data-testid="sos-call-988"><Phone size={18} />Call 988</a>
      <a href="sms:988" className="rounded-full bg-white/15 text-white font-semibold py-4 flex items-center justify-center gap-2" data-testid="sos-text-988"><MessageSquare size={18} />Text 988</a>
      <a href="sms:741741?&body=HOME" className="rounded-full bg-white/15 text-white font-semibold py-4" data-testid="sos-text-741741">Text HOME to 741741</a>
      <a href="tel:911" className="rounded-full bg-[#C2185B] text-white font-bold py-4" data-testid="sos-call-911">In immediate danger: call 911</a>
      <Link to="/safety-plan-now" className="rounded-full border border-white/30 text-white py-4 flex items-center justify-center gap-2" data-testid="sos-safety-plan"><Shield size={18} />Use my safety plan now</Link>
    </div>
  </div>
);

const Struggling = ({ onAgain }) => {
  const [state, setState] = useState(null);
  const message = async () => {
    setState({ busy: true });
    try { const { data } = await api.post("/sos/message-supporters"); setState({ msg: data.message }); }
    catch (e) { setState({ msg: navigator.onLine ? errMsg(e) : "You're offline. Messaging supporters needs a connection, so try calling someone you trust instead." }); }
  };
  return (
    <div className="w-full max-w-md text-center" data-testid="sos-struggling">
      <h2 className="text-2xl font-semibold text-white">That's okay. Hard moments pass, even when they don't feel like it.</h2>
      <div className="grid gap-3 mt-8">
        <MomentHelpers dark exclude="sos" />
        <OpenWhenNudge dark />
        <button onClick={message} disabled={state?.busy || !!state?.msg} className="rounded-full bg-[#7C5CBF] text-white font-semibold py-5 flex items-center justify-center gap-2 disabled:opacity-70" data-testid="sos-message-supporters">
          {state?.busy && <Loader2 size={18} className="animate-spin" />}Message my supporters
        </button>
        {state?.msg && <p className="text-[#D9CCF5]" role="status" data-testid="sos-message-result">{state.msg}</p>}
        <button onClick={onAgain} className="rounded-full bg-white/15 text-white font-semibold py-5 flex items-center justify-center gap-2" data-testid="sos-again"><RotateCcw size={18} />Another round</button>
        <p className="text-white/50 text-sm mt-2">Supporters get a short note asking them to check in. No other details are shared.</p>
      </div>
    </div>
  );
};

const Check = ({ onPick }) => (
  <div className="w-full max-w-md text-center" data-testid="sos-check">
    <p className="text-white/60 text-sm">Step 3 of 3</p>
    <h2 className="text-3xl font-semibold text-white mt-4">How are you now?</h2>
    <div className="grid gap-3 mt-8">
      <button onClick={() => onPick("calmer")} className="rounded-full bg-[#7C5CBF] text-white text-lg font-semibold py-5" data-testid="sos-answer-calmer">A bit calmer</button>
      <button onClick={() => onPick("struggling")} className="rounded-full bg-white/15 text-white text-lg font-semibold py-5" data-testid="sos-answer-struggling">Still struggling</button>
      <button onClick={() => onPick("unsafe")} className="rounded-full bg-[#C2185B]/80 text-white text-lg font-semibold py-5" data-testid="sos-answer-unsafe">I'm not safe</button>
    </div>
  </div>
);

const Calmer = () => (
  <div className="w-full max-w-md text-center" data-testid="sos-calmer">
    <div className="text-5xl" aria-hidden>🌙</div>
    <h2 className="text-2xl font-semibold text-white mt-4">You did that. Well done for taking a pause.</h2>
    <p className="text-white/70 mt-2">Be gentle with yourself for the rest of the day.</p>
    <div className="grid gap-3 mt-8">
      <Link to="/checkin" className="rounded-full bg-[#7C5CBF] text-white font-semibold py-4" data-testid="sos-checkin">Log a quick check-in</Link>
      <Link to="/" className="rounded-full bg-white/15 text-white font-semibold py-4" data-testid="sos-home">Back to Home</Link>
    </div>
  </div>
);

export default function SOS() {
  const nav = useNavigate();
  const [step, setStep] = useState("breathe");
  const logged = useRef(false);
  const toCheck = useRef(() => setStep("check")).current;
  const toGround = useRef(() => setStep("ground")).current;
  const pick = (o) => { if (!logged.current) { logged.current = true; logUse(o); } setStep(o); };
  const exit = () => { if (!logged.current) logUse("left"); window.history.length > 1 ? nav(-1) : nav("/"); };
  return (
    <div className="min-h-screen bg-[#16132B] flex flex-col" data-testid="sos-page">
      <div className="flex justify-end p-4"><button onClick={exit} className="w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center" aria-label="Leave calm screen" data-testid="sos-exit"><X size={20} /></button></div>
      <div className="flex-1 flex items-center justify-center px-6 pb-16 mm-fade-up" key={step}>
        {step === "breathe" && <Breathe onDone={toGround} />}
        {step === "ground" && <Ground onDone={toCheck} />}
        {step === "check" && <Check onPick={pick} />}
        {step === "calmer" && <Calmer />}
        {step === "struggling" && <Struggling onAgain={() => setStep("breathe")} />}
        {step === "unsafe" && <Unsafe />}
      </div>
    </div>
  );
}
